import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // The backend deliberately returns the same generic message
      // whether or not the email exists, so we surface the same
      // success state regardless. The toast keeps the user oriented
      // even if they accidentally double-submit.
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-[#FAFAFA]">
      <div className="hidden md:flex flex-col justify-between p-10 bg-[#09090B] text-white relative overflow-hidden">
        <Link to="/" className="inline-block bg-white rounded-xl p-3 shadow-sm self-start">
          <img
            src="/scanmint-logo.png"
            alt="ScanMint"
            className="h-12 w-auto block"
            draggable={false}
          />
        </Link>
        <div>
          <div className="mono-tiny text-white/50 mb-3">Forgot your password?</div>
          <h2 className="font-display text-4xl font-bold tracking-tight leading-tight">
            We'll email you a secure reset link.
          </h2>
          <p className="mt-4 text-white/60 max-w-sm">
            Reset links are single-use and expire after 30 minutes.
          </p>
        </div>
        <div className="mono-tiny text-white/40">© 2026 ScanMint</div>
      </div>

      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md space-y-6">
          <div className="md:hidden mb-4">
            <img
              src="/scanmint-logo.png"
              alt="ScanMint"
              className="h-12 w-auto select-none"
              draggable={false}
            />
          </div>

          {!submitted ? (
            <form
              onSubmit={submit}
              className="space-y-6"
              data-testid="forgot-password-form"
            >
              <div>
                <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                  Reset your password
                </h1>
                <p className="text-sm text-[#71717A] mt-2">
                  Enter the email associated with your account and we'll send a
                  reset link.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="mono-tiny text-[#71717A]">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="h-12"
                  data-testid="forgot-password-email"
                  required
                />
              </div>

              <Button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full h-12 bg-[#0F8F4F] hover:bg-[#0A6B3B] text-base"
                data-testid="forgot-password-submit"
              >
                {loading ? "Sending…" : "Send reset link"}
                <Mail className="w-4 h-4 ml-2" />
              </Button>

              <div className="text-sm">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-[#0F8F4F] font-medium hover:underline"
                  data-testid="forgot-password-back-to-login"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-6" data-testid="forgot-password-success">
              <div className="w-14 h-14 rounded-xl bg-[#0F8F4F]/10 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-[#0F8F4F]" strokeWidth={1.75} />
              </div>
              <div>
                <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
                  Check your email
                </h1>
                <p className="text-sm text-[#3F3F46] mt-3 leading-relaxed">
                  If an account exists, a reset link has been sent. The link
                  expires in 30 minutes and can only be used once.
                </p>
                <p className="text-xs text-[#71717A] mt-3">
                  Didn't receive it? Check spam, or{" "}
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="text-[#0F8F4F] font-medium hover:underline"
                    data-testid="forgot-password-try-again"
                  >
                    try a different email
                  </button>
                  .
                </p>
              </div>

              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-[#0F8F4F] font-medium hover:underline text-sm"
                data-testid="forgot-password-success-back-to-login"
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
